import { useState } from "react";
import type { DiscountCode } from "@cliqsuite/db";
import { useAdminData } from "../state/AdminDataContext";
import { fmtDateShort } from "../lib/format";
import { PageHeaderActions } from "./PageHeaderActions";
import { DiscountCodeModal } from "./DiscountCodeModal";

function valueLabel(c: DiscountCode): string {
  return c.discount_type === "percent" ? `${c.value}% off` : `MMK ${c.value.toLocaleString("en-US")} off`;
}

/**
 * Redeemable discount codes — an owner enters one at subscription-payment
 * time (BillingTab.tsx). redeemed_count only increments on payment
 * approval, not submission (see sync-api's /:id/approve).
 */
export function DiscountCodesPanel() {
  const { discountCodes, plans, codeSave, codeDelete, codeToggle } = useAdminData();
  const [modal, setModal] = useState<{ code: DiscountCode | null } | null>(null);

  const planName = (id: string | null) => (id ? (plans.find((p) => p.id === id)?.name ?? "Former plan") : "All plans");

  return (
    <section className="o-card" style={{ margin: 0 }}>
      <PageHeaderActions>
        <button type="button" className="o-btn o-btn--line" style={{ order: -1 }} onClick={() => setModal({ code: null })}>
          <span className="ms" aria-hidden="true">confirmation_number</span>
          New code
        </button>
      </PageHeaderActions>
      <div className="o-card__top">
        <div style={{ flex: 1 }}>
          <h2>Discount codes</h2>
          <div className="o-card__sub">Businesses enter these when they pay</div>
        </div>
      </div>
      {discountCodes.length === 0 && <div className="o-muted-note">No codes yet.</div>}
      {discountCodes.map((c) => {
        const exhausted = c.max_redemptions != null && c.redeemed_count >= c.max_redemptions;
        return (
          <div key={c.id} className="o-list-row" style={{ opacity: c.is_active && !exhausted ? 1 : 0.6 }}>
            <span className="o-ico o-ico--ok"><span className="ms" aria-hidden="true">confirmation_number</span></span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="o-row__title num" style={{ letterSpacing: 0.5 }}>{c.code}</div>
              <div className="o-row__sub">
                {[
                  valueLabel(c),
                  planName(c.plan_id),
                  `${c.redeemed_count}${c.max_redemptions != null ? ` of ${c.max_redemptions}` : ""} used`,
                  c.expires_at ? `until ${fmtDateShort(c.expires_at)}` : "no end date",
                ].join(", ")}
              </div>
            </div>
            <span className="o-list-row__state">{exhausted ? "Used up" : c.is_active ? "Active" : "Off"}</span>
            <button type="button" role="switch" aria-checked={c.is_active} aria-label={c.is_active ? "Turn off" : "Turn on"} className="o-toggle" onClick={() => codeToggle(c.id, !c.is_active)}>
              <span className="o-toggle__track" aria-hidden="true" />
            </button>
            <button type="button" className="o-icon-btn" aria-label="Edit" onClick={() => setModal({ code: c })}>
              <span className="ms" aria-hidden="true">edit</span>
            </button>
            <button type="button" className="o-icon-btn is-danger" aria-label="Delete" onClick={() => codeDelete(c.id)}>
              <span className="ms" aria-hidden="true">delete</span>
            </button>
          </div>
        );
      })}

      {modal && (
        <DiscountCodeModal
          code={modal.code}
          plans={plans}
          onClose={() => setModal(null)}
          onSubmit={(form) => {
            codeSave(modal.code?.id ?? null, form);
            setModal(null);
          }}
        />
      )}
    </section>
  );
}
